import React, { useMemo } from "react";
import { Modal, Row, Col, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import Select from "react-select";
import { createElementIdFactory } from "utils/elementIds";
import "./SLMAgencyModal.css";
import "containers/slm/lease/SLMLeaseDetail.css";

export default function SLMAgencyModal({
  show,
  onHide,
  agencySearchString,
  onAgencySearchStringChange,
  onAgencySearchKeyDown,
  onAgencySearch,
  agencyInterstateFlag,
  onAgencyInterstateFlagChange,
  createAgencyHref,
  editAgencyHref,
  showAddAnotherAgencyLink,
  onAddAnotherAgencyLink,
  selectedAgency,
  onAgencyChange,
  agencyOptions = [],
  agencyMenuIsOpen,
  agencyPhone,
  agencyLocale,
  selectedAgent,
  onAgentChange,
  agentOptions = [],
  isAgentOptionsLoading = false,
  agentPhone,
  agentEmail,
  showAddAnotherContact,
  onAddAnotherContact,
}) {
  const createId = useMemo(() => createElementIdFactory("slm-agency-modal"), []);
  const selectMenuPortalTarget = typeof window !== "undefined" ? document.body : null;
  const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2055 }),
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      id={createId("container")}
      dialogClassName="slm-agency-modal-wide"
      contentClassName="slm-agency-modal-content"
      fullscreen="lg-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>Agency + Agent</Modal.Title>
      </Modal.Header>
      <Modal.Body className="slm-agency-modal-body">
        <Form id={createId("form")} className="slm-agency-modal-form">
          <Row className="g-3 align-items-start slm-agency-modal-top-row">
            <Form.Group as={Col} xs={12} xl={7} controlId="agencySearchString">
              <Form.Label className="text-right" id={createId("field", "search-agencies", "label")}>Search Agencies + Agents</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  id={createId("field", "search-agencies", "input")}
                  onChange={onAgencySearchStringChange}
                  value={agencySearchString}
                  onKeyDown={onAgencySearchKeyDown}
                  as="input"
                  type="text"
                  placeholder="Search entities"
                />
                <Button id={createId("action", "search", "button")} onClick={onAgencySearch} variant="outline-secondary" size="sm" type="button">
                  Search
                </Button>
              </InputGroup>
              <div className="slm-agency-modal-interstate-check">
                <input
                  className="slm-agency-modal-interstate-input"
                  id={createId("field", "search-interstate", "checkbox")}
                  checked={agencyInterstateFlag}
                  onChange={onAgencyInterstateFlagChange}
                  type="checkbox"
                  name="agencyInterstateFlag"
                />
                <label className="slm-agency-modal-interstate-label" id={createId("field", "search-interstate", "label")} htmlFor={createId("field", "search-interstate", "checkbox")}>
                  Search interstate
                </label>
              </div>
            </Form.Group>
            <Col xs={12} xl={5} className="slm-agency-modal-actions-col">
              <div className="slm-agency-modal-actions">
                <a target="_blank" rel="noopener noreferrer" href={createAgencyHref}>
                  <Button id={createId("action", "create-agency", "button")} variant="primary" type="button" className="slm-compact-action-btn" size="m">Create New Agency</Button>
                </a>
                {editAgencyHref && (
                  <a target="_blank" rel="noopener noreferrer" href={editAgencyHref}>
                    <Button id={createId("action", "edit-agency", "button")} variant="primary" type="button" className="slm-compact-action-btn" size="m">Edit Agency</Button>
                  </a>
                )}
                {showAddAnotherAgencyLink && (
                  <Button id={createId("action", "add-agency-link", "button")} type="button" onClick={onAddAnotherAgencyLink} className="slm-compact-action-btn" variant="primary" size="m">
                    Add Another Agency Link
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          <Row className="g-3 align-items-start mt-1">
            <Form.Group as={Col} xs={12} xl={4} controlId="entity_org_id">
              <Form.Label className="text-right" id={createId("field", "agency", "label")}>Agency</Form.Label>
              <Select
                isClearable
                value={selectedAgency}
                onChange={onAgencyChange}
                options={agencyOptions}
                menuIsOpen={agencyMenuIsOpen}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={selectMenuStyles}
                inputId={createId("field", "agency", "input")}
                instanceId={createId("field", "agency", "instance")}
                isDisabled
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="agency_phone">
              <Form.Label className="text-right" id={createId("field", "agency-phone", "label")}>Agency Phone</Form.Label>
              <Form.Control id={createId("field", "agency-phone", "input")} value={agencyPhone} as="input" type="text" disabled readOnly />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="agency_locale">
              <Form.Label className="text-right" id={createId("field", "agency-locale", "label")}>Suburb State Postcode</Form.Label>
              <Form.Control id={createId("field", "agency-locale", "input")} value={agencyLocale} as="input" type="text" disabled readOnly />
            </Form.Group>
          </Row>

          <Row className="g-3 align-items-start mt-1">
            <Form.Group as={Col} xs={12} xl={4} controlId="entity_person_id">
              <Form.Label className="text-right" id={createId("field", "contact", "label")}>Contact</Form.Label>
              <Select
                isClearable
                value={selectedAgent}
                onChange={onAgentChange}
                options={agentOptions}
                isDisabled={isAgentOptionsLoading}
                isLoading={isAgentOptionsLoading}
                placeholder={isAgentOptionsLoading ? "Loading contacts..." : "Select..."}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={selectMenuStyles}
                inputId={createId("field", "contact", "input")}
                instanceId={createId("field", "contact", "instance")}
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="agent_phone">
              <Form.Label className="text-right" id={createId("field", "contact-phone", "label")}>Contact Phone</Form.Label>
              <Form.Control id={createId("field", "contact-phone", "input")} value={agentPhone} as="input" type="text" disabled readOnly />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="agent_email">
              <Form.Label className="text-right" id={createId("field", "contact-email", "label")}>Contact Email</Form.Label>
              <Form.Control id={createId("field", "contact-email", "input")} value={agentEmail} as="input" type="email" disabled readOnly />
            </Form.Group>
          </Row>

          {isAgentOptionsLoading && (
            <Row className="mt-2">
              <Col xs={12} className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" role="status" />
                <span>Loading agency contacts...</span>
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            <Col xs={12}>
              {showAddAnotherContact && (
                <Button id={createId("action", "add-contact", "button")} type="button" onClick={onAddAnotherContact} className="slm-compact-action-btn" variant="primary" size="m">
                  Add Another Contact
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
